import React from 'react';
import SurveyService from '../../../../services/SurveyService';
import useService from '../../../../hooks/useService';
import { useActiveProjectId } from '../../../../hooks';

const surveyService = new SurveyService();

export type UseSurveyResponseReturnValue = {
  survey: fhir.Questionnaire;
  response: fhir.QuestionnaireResponse;
  onResponseUpdate: (
    updatedResponse: fhir.QuestionnaireResponse,
    isValidResponse: boolean,
  ) => Promise<void>;
  isLoading: boolean;
  isUpdating: boolean;
  readError: boolean;
  writeError: boolean;
};

export const useSurveyResponse = (
  responseId: string,
): UseSurveyResponseReturnValue => {
  const [response, setResponse] =
    React.useState<fhir.QuestionnaireResponse | null>(null);
  const [survey, setSurvey] = React.useState<fhir.Questionnaire | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [isUpdating, setIsUpdating] = React.useState<boolean>(false);
  const [readError, setReadError] = React.useState<boolean>(false);
  const [writeError, setWriteError] = React.useState<boolean>(false);

  const projectId = useActiveProjectId();

  const [getResponse] = useService(surveyService.getResponse);
  const [updateResponse] = useService(surveyService.updateResponse);

  const fetchResponseAndSurvey = React.useCallback(async () => {
    setIsLoading(true);

    try {
      const responseData = await getResponse({
        projectId,
        responseId,
        includeSurvey: true,
      });

      if (!responseData?.data) {
        setReadError(true);
        return;
      }
      const { survey, ...response } = responseData.data as any;
      setResponse(response);
      setSurvey(survey);
    } catch (e) {
      setReadError(true);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, responseId, getResponse]);

  const onResponseUpdate = React.useCallback(
    async (
      updatedResponse: fhir.QuestionnaireResponse,
      isValidResponse: boolean,
    ) => {
      setResponse(updatedResponse);

      if (isValidResponse) {
        setIsUpdating(true);

        try {
          const updateData = await updateResponse(projectId, updatedResponse);
          setWriteError(!updateData?.data);
        } catch (e) {
          setWriteError(true);
        } finally {
          setIsUpdating(false);
        }
      }
    },
    [projectId, updateResponse],
  );

  React.useEffect(() => {
    fetchResponseAndSurvey();
  }, [fetchResponseAndSurvey]);

  return {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    survey,
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    response,
    isLoading,
    isUpdating,
    readError,
    writeError,
    onResponseUpdate,
  };
};
